import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ShieldCheck, BrainCircuit, ActivitySquare, ArrowUpRight, Check, X as XIcon,
  Sparkles, MessageCircle, BadgeCheck, Lock, Globe, Award, Zap,
  HeartHandshake, GraduationCap, ChevronDown, Menu, X,
} from "lucide-react";
import { PublicHeader, PublicFooter, FloatingWhatsApp as SharedFloatingWhatsApp, PublicLayoutStyles } from "@/components/PublicLayout";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_26d1aa6e-a958-43ca-9116-14748c74a37b/artifacts/lobzyet9_file_00000000236c71fdaa823d03db86abe9.png";

// ============ DATA ============
const MODULES = [
  {
    id: "assurances",
    name: "PHARMA ASSURANCES",
    tag: "Recouvrement & Paiements",
    icon: ShieldCheck,
    color: "emerald",
    features: [
      "Facturation OCR en 1 scan",
      "Suivi bordereaux & chèques en temps réel",
      "Relances automatiques CMU/CNAM",
      "Dashboard financier déchiffré",
      "Rapports mensuels IA (Claude)",
      "Détection factures non concordantes",
    ],
  },
  {
    id: "perimes",
    name: "PHARMA PÉRIMÉS",
    tag: "IA Prédictive & Stock",
    icon: BrainCircuit,
    color: "amber",
    features: [
      "Prédiction ruptures par commune",
      "Alertes péremption J-90 / J-60 / J-30",
      "Commandes suggérées automatiques",
      "Scanner de stock en temps réel",
      "Analytics saisonniers (palu, grippe)",
      "Base 96 DCI + 119 interactions",
    ],
  },
  {
    id: "dispensation",
    name: "PHARMA DISPENSATION",
    tag: "Copilot Officinal IA",
    icon: ActivitySquare,
    color: "sky",
    features: [
      "Copilot IA dispensation",
      "Contrôle posologie pédiatrique",
      "Scanner d'ordonnances",
      "Pharmacovigilance patient (QR code)",
      "Détection ordonnances frauduleuses",
      "Base interactions médicamenteuses",
    ],
  },
];

const PLANS = [
  {
    id: "essentiel",
    pack: "ESSENTIEL",
    subtitle: "1 module à la carte",
    price: 25000,
    saving: null,
    modules_count: 1,
    highlight: false,
    description: "Démarrez avec le module qui vous transforme le plus. Changez-le quand vous voulez.",
    cta: "Commencer l'essai",
  },
  {
    id: "duo",
    pack: "DUO",
    subtitle: "2 modules combinés",
    price: 45000,
    saving: 5000,
    modules_count: 2,
    highlight: false,
    description: "Deux leviers stratégiques pour officine en croissance. L'association la plus pragmatique.",
    cta: "Commencer l'essai",
  },
  {
    id: "institution",
    pack: "INSTITUTION",
    subtitle: "Les 3 modules · Plateforme complète",
    price: 65000,
    saving: 10000,
    modules_count: 3,
    highlight: true,
    description: "Le système nerveux digital complet. Choix des pharmaciens ambitieux qui pensent à long terme.",
    cta: "Commencer l'essai",
    badge: "LE PLUS CHOISI",
  },
];

const INCLUDED_IN_ALL_PLANS = [
  { icon: Sparkles, label: "30 jours d'essai gratuit, sans engagement" },
  { icon: HeartHandshake, label: "Satisfait ou remboursé 30 jours" },
  { icon: GraduationCap, label: "Formation initiale incluse" },
  { icon: MessageCircle, label: "Support WhatsApp 7j/7" },
  { icon: Lock, label: "Chiffrement AES-256 · Hébergement Afrique" },
  { icon: BadgeCheck, label: "Conformité aux normes pharmaceutiques ivoiriennes" },
];

const COMPARE_ROWS = [
  { label: "Facturation OCR & bordereaux", perimes: false, assurances: true, dispensation: false },
  { label: "Suivi recouvrement assureurs", perimes: false, assurances: true, dispensation: false },
  { label: "Relances CMU/CNAM automatiques", perimes: false, assurances: true, dispensation: false },
  { label: "IA prédictive ruptures de stock", perimes: true, assurances: false, dispensation: false },
  { label: "Alertes péremption J-90/60/30", perimes: true, assurances: false, dispensation: false },
  { label: "Commandes suggérées automatiques", perimes: true, assurances: false, dispensation: false },
  { label: "Copilot IA dispensation", perimes: false, assurances: false, dispensation: true },
  { label: "Contrôle posologie pédiatrique", perimes: false, assurances: false, dispensation: true },
  { label: "Scanner d'ordonnances", perimes: false, assurances: false, dispensation: true },
  { label: "Pharmacovigilance patient", perimes: false, assurances: false, dispensation: true },
  { label: "Dashboard analytics", perimes: true, assurances: true, dispensation: true },
  { label: "Rapports mensuels IA", perimes: true, assurances: true, dispensation: true },
  { label: "Mode hors ligne (PWA)", perimes: true, assurances: true, dispensation: true },
  { label: "Support WhatsApp 7j/7", perimes: true, assurances: true, dispensation: true },
];

const FAQS = [
  {
    q: "Comment fonctionne l'essai gratuit de 30 jours ?",
    a: "Inscrivez-vous en 2 minutes avec votre email professionnel. Aucune carte bancaire demandée. Vous avez accès à la totalité des modules que vous avez sélectionnés pendant 30 jours. À la fin de l'essai, vous choisissez de souscrire ou d'arrêter, sans aucune conséquence.",
  },
  {
    q: "Puis-je changer de pack ou de modules en cours d'abonnement ?",
    a: "Oui, librement. Vous pouvez ajouter ou retirer un module à tout moment depuis votre espace pharmacien. Le changement est effectif au prochain cycle de facturation, sans pénalité.",
  },
  {
    q: "Quels sont les modes de paiement acceptés ?",
    a: "Mobile Money (MTN, Orange, Moov, Wave), virement bancaire FCFA, carte bancaire Visa/Mastercard. Les paiements en Mobile Money sont privilégiés : ils sont instantanés, et nous n'appliquons aucuns frais supplémentaires.",
  },
  {
    q: "Que se passe-t-il si je ne suis pas satisfait ?",
    a: "Nous appliquons une garantie « satisfait ou remboursé » de 30 jours. Si notre solution ne vous convient pas dans le premier mois d'abonnement payant, nous vous remboursons intégralement sans poser de questions.",
  },
  {
    q: "Mes données patients sont-elles en sécurité ?",
    a: "Oui. Toutes les données sensibles (patients, assureurs, ordonnances) sont chiffrées au repos avec AES-256. Nos serveurs sont hébergés en Afrique, respectant les normes pharmaceutiques ivoiriennes. Seul·e votre équipe accède à vos données.",
  },
  {
    q: "Que se passe-t-il si je résilie ? Je perds mes données ?",
    a: "Non. À tout moment, vous pouvez exporter l'intégralité de vos données en PDF, CSV et Excel depuis votre espace. En cas de résiliation, vous disposez de 90 jours supplémentaires pour récupérer toutes vos données avant suppression définitive.",
  },
  {
    q: "Combien de postes de travail inclus par pack ?",
    a: "Chaque pack inclut un nombre illimité d'utilisateurs par officine. Le prix n'augmente pas avec le nombre de postes, contrairement à d'autres éditeurs.",
  },
  {
    q: "Est-ce que je peux gérer plusieurs officines avec un seul compte ?",
    a: "Oui, pour les chaînes ou réseaux multi-officines, contactez-nous pour un devis personnalisé. Nous proposons un plan réseau avec SuperAdmin, consolidation financière multi-sites et SLA 99.9%.",
  },
];

// ============ MOBILE MONEY SVG PICTOGRAMS ============
const MtnLogo = () => (
  <div className="w-16 h-16 rounded-full bg-[#FFCC00] flex items-center justify-center text-[#0A0A0A] font-black text-[11px] tracking-tighter shadow-sm border border-black/5">MTN</div>
);
const OrangeLogo = () => (
  <div className="w-16 h-16 bg-[#FF7900] flex items-center justify-center text-white font-black text-[10px] uppercase tracking-wider shadow-sm" style={{ borderRadius: "4px" }}>Orange</div>
);
const MoovLogo = () => (
  <div className="w-16 h-16 rounded-full bg-[#0038A8] flex items-center justify-center text-white font-black text-[11px] tracking-tighter shadow-sm">Moov</div>
);
const WaveLogo = () => (
  <div className="w-16 h-16 rounded-full bg-[#1DC7FF] flex items-center justify-center text-white font-black text-[11px] uppercase tracking-wider shadow-sm">Wave</div>
);
const VisaLogo = () => (
  <div className="w-20 h-12 bg-white border border-[#E5E4E2] rounded flex items-center justify-center">
    <span className="font-serif italic text-[#1A1F71] font-black text-lg tracking-tight">VISA</span>
  </div>
);
const MastercardLogo = () => (
  <div className="w-20 h-12 bg-white border border-[#E5E4E2] rounded flex items-center justify-center gap-0">
    <div className="w-6 h-6 bg-[#EB001B] rounded-full -mr-1.5"></div>
    <div className="w-6 h-6 bg-[#F79E1B] rounded-full opacity-90"></div>
  </div>
);

// ============ COMPONENTS ============

function Header() {
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#F9F8F6]/85 backdrop-blur-xl border-b border-[#E5E4E2]" data-testid="pricing-header">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-logo-link">
          <img src={LOGO_URL} alt="CMES" className="h-9 w-9 object-contain" />
          <div className="leading-tight">
            <p className="font-serif text-[17px] tracking-tight text-[#0A0A0A] font-semibold">CMES Pharma Intel</p>
            <p className="text-[9px] uppercase tracking-[0.2em] text-[#4A4D4C]">Institution officinale</p>
          </div>
        </Link>
        <nav className="hidden lg:flex items-center gap-10">
          <Link to="/#products" className="text-[13px] font-medium text-[#0A0A0A]/75 hover:text-[#025946] transition-colors">Produits</Link>
          <Link to="/#trust" className="text-[13px] font-medium text-[#0A0A0A]/75 hover:text-[#025946] transition-colors">Conformité</Link>
          <Link to="/tarifs" className="text-[13px] font-semibold text-[#025946] transition-colors">Tarifs</Link>
          <Link to="/#vision" className="text-[13px] font-medium text-[#0A0A0A]/75 hover:text-[#025946] transition-colors">Vision</Link>
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <button onClick={() => nav("/login")} className="px-5 py-2.5 text-[13px] font-semibold text-[#0A0A0A] hover:text-[#025946]" data-testid="header-login-btn">Espace Pharmacien</button>
          <button onClick={() => nav("/register")} className="group px-6 py-2.5 bg-[#0A0A0A] hover:bg-[#025946] text-white text-[13px] font-semibold flex items-center gap-2 transition-colors" data-testid="header-cta-btn">
            Essai 30 jours
          </button>
        </div>
        <button className="lg:hidden" onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
      </div>
      {menuOpen && (
        <div className="lg:hidden bg-[#F9F8F6] border-t border-[#E5E4E2] px-6 py-6 space-y-4">
          <Link to="/#products" onClick={() => setMenuOpen(false)} className="block text-sm font-medium">Produits</Link>
          <Link to="/#trust" onClick={() => setMenuOpen(false)} className="block text-sm font-medium">Conformité</Link>
          <Link to="/tarifs" onClick={() => setMenuOpen(false)} className="block text-sm font-medium text-[#025946]">Tarifs</Link>
          <button onClick={() => nav("/register")} className="w-full px-6 py-3 bg-[#0A0A0A] text-white text-sm font-semibold">Essai 30 jours</button>
        </div>
      )}
    </header>
  );
}

function PricingHero() {
  return (
    <section className="relative bg-[#F9F8F6] overflow-hidden pt-32 pb-16 lg:pt-40 lg:pb-24" data-testid="pricing-hero">
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: "linear-gradient(to right, #0A0A0A 1px, transparent 1px), linear-gradient(to bottom, #0A0A0A 1px, transparent 1px)",
        backgroundSize: "80px 80px",
        opacity: 0.02
      }}></div>
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">
          ─── Tarifs · Transparents · Modulaires
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.02em] text-[#0A0A0A] mb-6 max-w-4xl">
          L'investissement qui se <em className="italic font-light text-[#025946]">rentabilise</em> <br />dès le premier mois.
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="text-[17px] lg:text-[19px] text-[#4A4D4C] max-w-2xl font-light leading-relaxed">
          Choisissez <span className="text-[#0A0A0A] font-normal">1, 2 ou 3 modules</span> selon vos besoins. Payez uniquement ce que vous utilisez. Sans engagement.
        </motion.p>
      </div>
    </section>
  );
}

function PlanCard({ plan, index }) {
  const nav = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className={`relative bg-white border transition-all duration-500 flex flex-col ${
        plan.highlight
          ? "border-[#025946] shadow-2xl shadow-[#025946]/10 lg:scale-[1.02] z-10"
          : "border-[#E5E4E2] hover:border-[#025946]"
      }`}
      data-testid={`plan-card-${plan.id}`}
    >
      {plan.badge && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#C9A25E] text-[#0A0A0A] text-[10px] font-extrabold uppercase tracking-[0.2em] px-4 py-1.5 shadow-lg" data-testid={`plan-badge-${plan.id}`}>
          ★ {plan.badge}
        </div>
      )}
      <div className="p-8 lg:p-10 flex flex-col flex-1">
        <div className="mb-6">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-3">PACK</p>
          <h3 className="font-serif text-4xl lg:text-5xl font-light tracking-tight text-[#0A0A0A] mb-2">{plan.pack}</h3>
          <p className="text-[13px] text-[#4A4D4C] font-medium">{plan.subtitle}</p>
        </div>

        <div className="py-6 border-y border-[#E5E4E2] mb-6">
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-6xl lg:text-7xl font-light tracking-tight text-[#0A0A0A]">{plan.price.toLocaleString("fr-FR")}</span>
            <span className="text-base text-[#4A4D4C] font-semibold">FCFA</span>
          </div>
          <p className="text-[12px] text-[#4A4D4C] mt-2 uppercase tracking-wider">Par mois · Hors taxes</p>
          {plan.saving && (
            <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#025946] bg-[#025946]/8 px-3 py-1 border border-[#025946]/20">
              <Zap className="w-3 h-3" /> Économie {plan.saving.toLocaleString("fr-FR")} FCFA / mois
            </p>
          )}
        </div>

        <p className="text-[13px] text-[#4A4D4C] leading-relaxed mb-6 font-light">{plan.description}</p>

        <div className="mb-6 pb-6 border-b border-[#E5E4E2]">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-[#0A0A0A] mb-4">
            {plan.modules_count === 1 ? "1 module au choix" : plan.modules_count === 2 ? "2 modules au choix" : "Les 3 modules inclus"}
          </p>
          <ul className="space-y-2.5">
            {MODULES.slice(0, plan.modules_count).map((m, i) => (
              <li key={m.id} className="flex items-start gap-3 text-[13px]">
                <div className="w-5 h-5 rounded-full bg-[#025946]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <m.icon className="w-3 h-3 text-[#025946]" strokeWidth={2.5} />
                </div>
                <span className="text-[#0A0A0A] font-medium">
                  {plan.modules_count >= 3 ? m.name : <em className="text-[#025946] italic">Votre choix #{i + 1}</em>}
                </span>
              </li>
            ))}
            {plan.modules_count < 3 && (
              <li className="flex items-start gap-3 text-[13px] text-[#4A4D4C]/60">
                <div className="w-5 h-5 rounded-full bg-[#E5E4E2] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <XIcon className="w-3 h-3 text-[#4A4D4C]/40" strokeWidth={2.5} />
                </div>
                <span>Les {3 - plan.modules_count} autre{3 - plan.modules_count > 1 ? "s" : ""} module{3 - plan.modules_count > 1 ? "s" : ""} non inclus</span>
              </li>
            )}
          </ul>
        </div>

        <ul className="space-y-2 mb-8">
          {[
            "Utilisateurs illimités",
            "Support WhatsApp 7j/7",
            "Formation initiale incluse",
            "Mode hors ligne (PWA)",
            "Mises à jour régulières",
          ].map((f) => (
            <li key={f} className="flex items-start gap-2 text-[12px] text-[#4A4D4C]">
              <Check className="w-3.5 h-3.5 text-[#025946] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <button
          onClick={() => nav("/register")}
          data-testid={`plan-cta-${plan.id}`}
          className={`group w-full py-4 text-[13px] font-semibold flex items-center justify-center gap-2 transition-all mt-auto ${
            plan.highlight
              ? "bg-[#025946] hover:bg-[#037A60] text-white"
              : "bg-[#0A0A0A] hover:bg-[#025946] text-white"
          }`}
        >
          {plan.cta}
          <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </button>

        <p className="mt-3 text-center text-[11px] text-[#4A4D4C]/80">30 jours · Sans CB · Sans engagement</p>
      </div>
    </motion.div>
  );
}

function Plans() {
  return (
    <section className="relative bg-[#F9F8F6] pb-24" data-testid="plans-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {PLANS.map((p, i) => (
            <PlanCard key={p.id} plan={p} index={i} />
          ))}
        </div>
        <div className="mt-12 text-center">
          <p className="text-[13px] text-[#4A4D4C] font-light">
            Besoin d'une solution pour une chaîne multi-officines ?{" "}
            <a href="https://wa.me/2250768127000" target="_blank" rel="noopener noreferrer" className="text-[#025946] font-semibold underline hover:text-[#037A60] transition-colors" data-testid="contact-network-link">
              Demander un devis Réseau
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}

function Modules() {
  return (
    <section className="relative bg-white py-24 lg:py-32 border-y border-[#E5E4E2]" data-testid="modules-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-6 mb-16">
          <div className="col-span-12 lg:col-span-5">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">─── Modules disponibles</p>
            <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] tracking-tight text-[#0A0A0A]">
              Composez <em className="italic font-light">votre pack</em>.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-[15px] text-[#4A4D4C] leading-relaxed font-light">
              Chaque module est une solution verticale autonome. Commencez par celui qui vous débloque le plus — ajoutez-en d'autres quand vous êtes prêt·e. Le prix s'ajuste automatiquement.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {MODULES.map((m, i) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="border border-[#E5E4E2] bg-[#F9F8F6] p-8 hover:border-[#025946] hover:shadow-lg transition-all"
              data-testid={`module-card-${m.id}`}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#025946]/8 border border-[#025946]/20 flex items-center justify-center">
                  <m.icon className="w-5 h-5 text-[#025946]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-[#025946]">{m.tag}</p>
                </div>
              </div>
              <h3 className="font-serif text-2xl lg:text-3xl font-light text-[#0A0A0A] mb-6 leading-tight">
                {m.name.split(" ").map((w, j) => (
                  <span key={j} className={j === 1 ? "italic" : ""}>{w} </span>
                ))}
              </h3>
              <ul className="space-y-2.5">
                {m.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-[13px] text-[#4A4D4C]">
                    <Check className="w-3.5 h-3.5 text-[#025946] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Compare() {
  return (
    <section className="relative bg-[#F9F8F6] py-24 lg:py-32" data-testid="compare-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="mb-12">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">─── Comparateur détaillé</p>
          <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] tracking-tight text-[#0A0A0A]">
            Chaque module, <em className="italic font-light">décortiqué</em>.
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-white border border-[#E5E4E2]" data-testid="compare-table">
            <thead>
              <tr>
                <th className="text-left p-5 border-b border-[#E5E4E2] font-semibold text-[12px] uppercase tracking-wider text-[#4A4D4C] w-1/2">Fonctionnalité</th>
                <th className="p-5 border-b border-l border-[#E5E4E2] text-center">
                  <div className="flex flex-col items-center gap-1">
                    <ShieldCheck className="w-5 h-5 text-[#025946]" strokeWidth={1.5} />
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#0A0A0A]">ASSURANCES</span>
                  </div>
                </th>
                <th className="p-5 border-b border-l border-[#E5E4E2] text-center">
                  <div className="flex flex-col items-center gap-1">
                    <BrainCircuit className="w-5 h-5 text-[#025946]" strokeWidth={1.5} />
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#0A0A0A]">PÉRIMÉS</span>
                  </div>
                </th>
                <th className="p-5 border-b border-l border-[#E5E4E2] text-center">
                  <div className="flex flex-col items-center gap-1">
                    <ActivitySquare className="w-5 h-5 text-[#025946]" strokeWidth={1.5} />
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#0A0A0A]">DISPENSATION</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F8F6]"}>
                  <td className="p-4 border-b border-[#E5E4E2] text-[13px] text-[#0A0A0A] font-medium">{row.label}</td>
                  <td className="p-4 border-b border-l border-[#E5E4E2] text-center">
                    {row.assurances ? <Check className="w-5 h-5 text-[#025946] mx-auto" strokeWidth={2.5} /> : <XIcon className="w-4 h-4 text-[#E5E4E2] mx-auto" strokeWidth={2} />}
                  </td>
                  <td className="p-4 border-b border-l border-[#E5E4E2] text-center">
                    {row.perimes ? <Check className="w-5 h-5 text-[#025946] mx-auto" strokeWidth={2.5} /> : <XIcon className="w-4 h-4 text-[#E5E4E2] mx-auto" strokeWidth={2} />}
                  </td>
                  <td className="p-4 border-b border-l border-[#E5E4E2] text-center">
                    {row.dispensation ? <Check className="w-5 h-5 text-[#025946] mx-auto" strokeWidth={2.5} /> : <XIcon className="w-4 h-4 text-[#E5E4E2] mx-auto" strokeWidth={2} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function PaymentMethods() {
  return (
    <section className="relative bg-white py-24 border-y border-[#E5E4E2]" data-testid="payment-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-6 items-center">
          <div className="col-span-12 lg:col-span-5">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">─── Paiement · Libre & Local</p>
            <h2 className="font-serif text-3xl lg:text-5xl leading-[1.05] tracking-tight text-[#0A0A0A] mb-4">
              Payez comme <em className="italic font-light">vous voulez</em>.
            </h2>
            <p className="text-[14px] text-[#4A4D4C] leading-relaxed font-light">
              Mobile Money instantané, virement FCFA, carte internationale. Aucun surcoût, aucune commission cachée.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 lg:gap-6 items-center">
              <div className="flex flex-col items-center gap-2" data-testid="payment-mtn">
                <MtnLogo />
                <span className="text-[10px] uppercase tracking-wider text-[#4A4D4C] font-semibold">MTN</span>
              </div>
              <div className="flex flex-col items-center gap-2" data-testid="payment-orange">
                <OrangeLogo />
                <span className="text-[10px] uppercase tracking-wider text-[#4A4D4C] font-semibold">Orange</span>
              </div>
              <div className="flex flex-col items-center gap-2" data-testid="payment-moov">
                <MoovLogo />
                <span className="text-[10px] uppercase tracking-wider text-[#4A4D4C] font-semibold">Moov</span>
              </div>
              <div className="flex flex-col items-center gap-2" data-testid="payment-wave">
                <WaveLogo />
                <span className="text-[10px] uppercase tracking-wider text-[#4A4D4C] font-semibold">Wave</span>
              </div>
              <div className="flex flex-col items-center gap-2" data-testid="payment-visa">
                <VisaLogo />
                <span className="text-[10px] uppercase tracking-wider text-[#4A4D4C] font-semibold">Visa</span>
              </div>
              <div className="flex flex-col items-center gap-2" data-testid="payment-mastercard">
                <MastercardLogo />
                <span className="text-[10px] uppercase tracking-wider text-[#4A4D4C] font-semibold">Mastercard</span>
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-[#E5E4E2] flex flex-wrap gap-x-6 gap-y-2 text-[11px] text-[#4A4D4C] uppercase tracking-wider">
              <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#025946]" strokeWidth={2.5} /> Virement FCFA</span>
              <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#025946]" strokeWidth={2.5} /> Facture électronique</span>
              <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#025946]" strokeWidth={2.5} /> Reçu officiel</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Promises() {
  return (
    <section className="relative bg-[#0A0A0A] text-[#F9F8F6] py-24 lg:py-32 overflow-hidden" data-testid="promises-section">
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: "radial-gradient(circle at 80% 20%, #C9A25E 1px, transparent 1px)",
        backgroundSize: "40px 40px"
      }}></div>
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="mb-14">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#C9A25E] mb-6">─── Nos engagements</p>
          <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
            Des promesses <em className="italic font-light text-[#C9A25E]">que nous tenons</em>.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {INCLUDED_IN_ALL_PLANS.map((p, i) => (
            <div key={i} className="bg-[#0A0A0A] p-8 flex items-start gap-4" data-testid={`promise-${i}`}>
              <div className="w-10 h-10 bg-[#C9A25E]/10 border border-[#C9A25E]/30 flex items-center justify-center flex-shrink-0">
                <p.icon className="w-5 h-5 text-[#C9A25E]" strokeWidth={1.5} />
              </div>
              <p className="text-[14px] leading-[1.5] text-white pt-1.5">{p.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#E5E4E2]" data-testid={`faq-${index}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full py-6 flex items-center justify-between text-left gap-6 hover:text-[#025946] transition-colors"
        data-testid={`faq-toggle-${index}`}
      >
        <span className="font-serif text-xl lg:text-2xl font-light text-[#0A0A0A] pr-4">{item.q}</span>
        <ChevronDown className={`w-5 h-5 text-[#025946] flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>
      {open && (
        <div className="pb-6 text-[14px] text-[#4A4D4C] leading-[1.7] font-light max-w-3xl" data-testid={`faq-answer-${index}`}>
          {item.a}
        </div>
      )}
    </div>
  );
}

function Faq() {
  return (
    <section className="relative bg-[#F9F8F6] py-24 lg:py-32" data-testid="faq-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-6 lg:gap-12">
          <div className="col-span-12 lg:col-span-4 mb-10 lg:mb-0">
            <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">─── Questions fréquentes</p>
            <h2 className="font-serif text-3xl lg:text-5xl leading-[1.05] tracking-tight text-[#0A0A0A] mb-6">
              Tout ce que vous <br /><em className="italic font-light">devez savoir</em>.
            </h2>
            <p className="text-[13px] text-[#4A4D4C] leading-relaxed font-light">
              Une autre question ? Parlez-en avec un expert CMES 7j/7.
            </p>
            <a href="https://wa.me/2250768127000" target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-[13px] font-semibold text-[#025946] hover:text-[#037A60] transition-colors" data-testid="faq-whatsapp-link">
              <MessageCircle className="w-4 h-4" /> Parler à un expert
            </a>
          </div>
          <div className="col-span-12 lg:col-span-8">
            {FAQS.map((item, i) => <FaqItem key={i} item={item} index={i} />)}
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  const nav = useNavigate();
  return (
    <section className="relative bg-[#F9F8F6] py-24 lg:py-32" data-testid="final-cta-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="border-t border-b border-[#0A0A0A] py-16 lg:py-20 px-6 lg:px-16 relative">
          <div className="absolute top-0 left-0 w-4 h-4 border-l border-t border-[#025946] -translate-x-[1px] -translate-y-[1px]"></div>
          <div className="absolute top-0 right-0 w-4 h-4 border-r border-t border-[#025946] translate-x-[1px] -translate-y-[1px]"></div>
          <div className="absolute bottom-0 left-0 w-4 h-4 border-l border-b border-[#025946] -translate-x-[1px] translate-y-[1px]"></div>
          <div className="absolute bottom-0 right-0 w-4 h-4 border-r border-b border-[#025946] translate-x-[1px] translate-y-[1px]"></div>
          <div className="grid grid-cols-12 gap-6 items-center">
            <div className="col-span-12 lg:col-span-8">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#025946] mb-6">─── Prêt·e à commencer ?</p>
              <h2 className="font-serif text-4xl lg:text-6xl leading-[1.05] tracking-tight text-[#0A0A0A] mb-5">
                30 jours gratuits. <em className="italic font-light">Zéro risque.</em>
              </h2>
              <p className="text-[14px] text-[#4A4D4C] leading-relaxed max-w-2xl font-light">
                Sans carte bancaire. Sans engagement. Satisfait ou remboursé.
                Commencez aujourd'hui, transformez votre officine dès demain.
              </p>
            </div>
            <div className="col-span-12 lg:col-span-4 flex flex-col gap-3 lg:items-end">
              <button onClick={() => nav("/register")} className="group px-8 py-4 bg-[#0A0A0A] hover:bg-[#025946] text-white text-[14px] font-semibold flex items-center justify-center gap-3 transition-all" data-testid="final-cta-register">
                Commencer l'essai gratuit
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </button>
              <a href="https://wa.me/2250768127000" target="_blank" rel="noopener noreferrer" className="px-8 py-4 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-[14px] font-semibold flex items-center justify-center gap-3 transition-all" data-testid="final-cta-whatsapp">
                <MessageCircle className="w-5 h-5" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0A0A0A] text-[#F9F8F6] pt-16 pb-10" data-testid="pricing-footer">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-8 mb-12">
          <div className="col-span-12 lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="CMES" className="h-10 w-10 object-contain" />
              <div>
                <p className="font-serif text-xl font-semibold">CMES Pharma Intel</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">Institution officinale</p>
              </div>
            </div>
            <p className="text-[13px] text-white/60 leading-[1.7] max-w-sm">
              Le partenaire stratégique de santé publique des pharmaciens d'Afrique francophone. Abidjan, Côte d'Ivoire.
            </p>
          </div>
          <div className="col-span-6 lg:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A25E] font-bold mb-6">Navigation</p>
            <ul className="space-y-3 text-[13px] text-white/60">
              <li><Link to="/" className="hover:text-white transition-colors">Accueil</Link></li>
              <li><Link to="/tarifs" className="hover:text-white transition-colors">Tarifs</Link></li>
              <li><Link to="/#trust" className="hover:text-white transition-colors">Conformité</Link></li>
            </ul>
          </div>
          <div className="col-span-6 lg:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A25E] font-bold mb-6">Espace</p>
            <ul className="space-y-3 text-[13px] text-white/60">
              <li><Link to="/login" className="hover:text-white transition-colors">Se connecter</Link></li>
              <li><Link to="/register" className="hover:text-white transition-colors">Essai 30 jours</Link></li>
            </ul>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A25E] font-bold mb-6">Contact</p>
            <ul className="space-y-3 text-[13px] text-white/60">
              <li>Abidjan, Côte d'Ivoire</li>
              <li>+225 07 68 12 70 00 · Lun-Dim 7j/7</li>
              <li><a href="https://wa.me/2250768127000" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">WhatsApp</a></li>
            </ul>
          </div>
        </div>
        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[11px] text-white/40">© 2026 CMES Pharma Intel. Institution privée de santé digitale.</p>
          <div className="flex items-center gap-6 text-[11px] text-white/40">
            <span className="flex items-center gap-1.5"><Lock className="w-3 h-3" /> AES-256</span>
            <span className="flex items-center gap-1.5"><Globe className="w-3 h-3" /> Afrique</span>
            <span className="flex items-center gap-1.5"><Award className="w-3 h-3" /> Fait en Côte d'Ivoire</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FloatingWhatsApp() {
  return (
    <a href="https://wa.me/2250768127000" target="_blank" rel="noopener noreferrer" data-testid="whatsapp-support-floating"
      className="fixed bottom-6 left-6 z-40 group flex items-center gap-3 px-5 py-3 bg-[#025946] hover:bg-[#037A60] text-white shadow-2xl transition-all hover:scale-105">
      <MessageCircle className="w-4 h-4" />
      <span className="text-[12px] font-semibold hidden sm:inline">Parler à un expert · 7j/7</span>
    </a>
  );
}

// ============ MAIN ============
export default function PricingPage() {
  return (
    <div className="cmes-landing min-h-screen bg-[#F9F8F6] antialiased" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <Helmet
        title="Tarifs CMES Pharma Intel | À partir de 25 000 FCFA/mois · Essai 30 jours"
        meta={[
          { name: "description", content: "Tarifs modulaires CMES Pharma Intel : 25 000 FCFA/mois (1 module), 45 000 FCFA (2 modules), 65 000 FCFA (3 modules). Essai 30 jours gratuit. Mobile Money accepté." },
          { property: "og:title", content: "Tarifs CMES Pharma Intel | Premium & Transparent" },
          { property: "og:description", content: "Choisissez 1, 2 ou 3 modules. Payez uniquement ce que vous utilisez. 30 jours d'essai gratuit." },
        ]}
        link={[{ rel: "canonical", href: "https://cmespharmaintel.com/tarifs" }]}
      />
      <PublicLayoutStyles />
      <PublicHeader activeKey="Tarifs" />
      <main>
        <PricingHero />
        <Plans />
        <Modules />
        <Compare />
        <PaymentMethods />
        <Promises />
        <Faq />
        <FinalCta />
      </main>
      <PublicFooter />
      <SharedFloatingWhatsApp />
    </div>
  );
}
